import { Stack } from '@chakra-ui/react';
import DatePickerComponent from '@components/DatePickerComponent/DatePickerComponent';
import InputComponent from '@components/InputComponent/InputComponent';
import RadioComponent from '@components/RadioComponent/RadioComponent';
import SmallSectionTitleComponent from '@components/SmallSectionTitleComponent/SmallSectionTitleComponent';
import UploadComponent from '@components/UploadComponent/UploadComponent';
import { StoreContext } from '@contexts/StoreContext';
import { PaymentTypesTag } from '@utils/enum/PaymentTypesTag';
import { fileToBase64, validateIban } from '@utils/functions';
import { FC, useCallback, useContext, useMemo } from 'react';
import { UseFormRegister, FieldValues, FieldErrors, Control } from 'react-hook-form';
import { BankTransfer, CreditCard, PaymentMethod, TravelAccount } from 'types/formData';
import { RadioItem } from 'types/formElement';
import { StoreContextType } from 'types/reducers';

interface Props {
    id: string;
    register: UseFormRegister<FieldValues>;
    control: Control<FieldValues>;
    paymentMethod: PaymentMethod;
    updateId: (id: number, object: CreditCard | BankTransfer | TravelAccount | null) => void;
    updatePaymentMethodData: (data: CreditCard | BankTransfer | TravelAccount | null) => void;
    errors: FieldErrors<FieldValues>;
    labelRadio?: string;
    showTravelAccount?: boolean;
    isRequired?: boolean;
    showRiba?: boolean;
    isRequiredCreditCardData?: boolean;
    isRequiredBankData?: boolean;
}

const PaymentMethodForm: FC<Props> = ({
    id,
    register,
    control,
    paymentMethod,
    updateId,
    updatePaymentMethodData,
    errors,
    labelRadio = 'Metodi di pagamento',
    showTravelAccount = true,
    isRequired = false,
    showRiba = true,
    isRequiredCreditCardData = true,
    isRequiredBankData = true,
}) => {
    const { state } = useContext<StoreContextType>(StoreContext);

    const handlePaymentMethodId = useCallback(
        (selectedId: string) => {
            const foundMethod = state.paymentType.find((type) => type.id === parseInt(selectedId));
            if (foundMethod) {
                let paymentMethodObject: CreditCard | BankTransfer | TravelAccount | null = null;
                switch (foundMethod.tag) {
                    case PaymentTypesTag.CREDIT_CARD:
                        paymentMethodObject = {
                            cardNumber: '',
                            expirationDate: '',
                            cvc: '',
                            firstName: '',
                            lastName: '',
                        };
                        break;
                    case PaymentTypesTag.SDD:
                    case PaymentTypesTag.RIBA:
                    case PaymentTypesTag.BANK_TRANSFER:
                    case PaymentTypesTag.IBAN_FOR_RIBA_OR_SDD:
                        paymentMethodObject = {
                            iban: '',
                            bankAccount: '',
                            swift: '',
                        };
                        break;
                    case PaymentTypesTag.TRAVEL_FUND:
                        paymentMethodObject = {
                            attachments: [],
                        };
                        break;
                    default:
                        paymentMethodObject = null;
                        break;
                }
                updateId(parseInt(selectedId), paymentMethodObject);
            }
        },
        [state.paymentType, updateId],
    );

    const handleUpdatePaymentMethod = useCallback(
        (
            value: string,
            key: 'iban' | 'bankAccount' | 'swift' | 'cardNumber' | 'expirationDate' | 'cvc' | 'firstName' | 'lastName',
        ) => {
            if (key && paymentMethod.paymentData) {
                updatePaymentMethodData({ ...paymentMethod.paymentData, [key]: value });
            }
        },
        [paymentMethod.paymentData, updatePaymentMethodData],
    );

    const handleUpdateTravelAccountFile = useCallback(
        async (files: File[]) => {
            if (files) {
                const typeId = state.attachmentTypes.find((at) => at.tag === 'PAYMENT_TRAVEL_DOC')?.id ?? 0;
                const attachments = [];
                for (let i = 0; i < files.length; i++) {
                    const filestream = (await fileToBase64(files[0])) as string;
                    const name = files[0].name;
                    const size = files[0].size;
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const mimeType = files[0].type;
                    attachments.push({ filestream: filestream.split(',')[1], name, size, mimeType, typeId });
                }
                updatePaymentMethodData({
                    ...paymentMethod.paymentData,
                    attachments,
                });
            }
        },
        [paymentMethod.paymentData, state.attachmentTypes, updatePaymentMethodData],
    );

    const handleDeleteTravelAccountFile = useCallback(() => {
        updatePaymentMethodData({
            ...paymentMethod.paymentData,
            attachments: [],
        });
    }, [paymentMethod.paymentData]);

    const paymentMethodsOptions: RadioItem[] = useMemo(() => {
        let filteredPaymentType = state.paymentType;
        if (!showTravelAccount) {
            filteredPaymentType = filteredPaymentType.filter((type) => type.tag != PaymentTypesTag.TRAVEL_FUND);
        }
        if (!showRiba) {
            filteredPaymentType = filteredPaymentType.filter((type) => type.tag != PaymentTypesTag.RIBA);
        }
        return filteredPaymentType.map((type) => {
            return {
                id: type.id,
                label: type.name,
            };
        });
    }, [state, showTravelAccount, showRiba]);

    const paymentMethodForm = useMemo(() => {
        const foundMethod = state.paymentType.find((type) => type.id === paymentMethod.paymentMethodId);
        if (foundMethod) {
            const paymentData = paymentMethod.paymentData;
            switch (foundMethod.tag) {
                case PaymentTypesTag.CREDIT_CARD:
                    if (!isRequiredCreditCardData) {
                        return null;
                    }
                    return (
                        <>
                            <SmallSectionTitleComponent title={foundMethod.name} />
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id={`${id}_cardNumber`}
                                    label="Numero di carta di credito"
                                    type="number"
                                    register={register}
                                    value={(paymentData as CreditCard).cardNumber}
                                    onChange={(value) => handleUpdatePaymentMethod(value, 'cardNumber')}
                                    isInvalid={errors[`${id}_cardNumber`] ?? null}
                                    minLength={13}
                                    maxLength={16}
                                    isRequired
                                    flex={2.2}
                                />
                                <DatePickerComponent
                                    id={`${id}_expirationDate`}
                                    label="Data di scadenza"
                                    isInvalid={errors[`${id}_expirationDate`] ?? null}
                                    value={(paymentData as CreditCard).expirationDate}
                                    onChangeProp={(value) =>
                                        handleUpdatePaymentMethod(value.toString(), 'expirationDate')
                                    }
                                    isRequired
                                    control={control}
                                />
                                <InputComponent
                                    id={`${id}_cvc`}
                                    label="CVV/CVC"
                                    type="number"
                                    register={register}
                                    value={(paymentData as CreditCard).cvc}
                                    onChange={(value) => handleUpdatePaymentMethod(value, 'cvc')}
                                    isInvalid={errors[`${id}_cvc`] ?? null}
                                    minLength={3}
                                    maxLength={3}
                                    isRequired
                                    flex={1}
                                />
                            </Stack>
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id={`${id}_paymentMethodFirstName`}
                                    label="Nome"
                                    register={register}
                                    value={(paymentData as CreditCard).firstName}
                                    onChange={(value) => handleUpdatePaymentMethod(value, 'firstName')}
                                    isInvalid={errors[`${id}_paymentMethodFirstName`] ?? null}
                                    minLength={1}
                                    isRequired
                                />
                                <InputComponent
                                    id={`${id}_paymentMethodLastName`}
                                    label="Cognome"
                                    register={register}
                                    value={(paymentData as CreditCard).lastName}
                                    onChange={(value) => handleUpdatePaymentMethod(value, 'lastName')}
                                    isInvalid={errors[`${id}_paymentMethodLastName`] ?? null}
                                    minLength={1}
                                    isRequired
                                />
                            </Stack>
                        </>
                    );
                case PaymentTypesTag.SDD:
                case PaymentTypesTag.RIBA:
                case PaymentTypesTag.BANK_TRANSFER:
                case PaymentTypesTag.IBAN_FOR_RIBA_OR_SDD:
                    if (foundMethod.tag === PaymentTypesTag.BANK_TRANSFER && !isRequiredBankData) {
                        return null;
                    }
                    return (
                        <>
                            <SmallSectionTitleComponent title={foundMethod.name} />
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id={`${id}_iban`}
                                    label="IBAN"
                                    register={register}
                                    value={(paymentData as BankTransfer).iban}
                                    onChange={(value) => handleUpdatePaymentMethod(value, 'iban')}
                                    isInvalid={errors[`${id}_iban`] ?? null}
                                    minLength={16}
                                    isRequired
                                    flex={2.2}
                                    validate={validateIban}
                                />
                                <InputComponent
                                    id={`${id}_bankAccount`}
                                    label="Conto/Corrente"
                                    register={register}
                                    value={(paymentData as BankTransfer).bankAccount}
                                    onChange={(value) => handleUpdatePaymentMethod(value, 'bankAccount')}
                                    isInvalid={errors[`${id}_bankAccount`] ?? null}
                                    minLength={12}
                                    maxLength={12}
                                    isRequired
                                    flex={1}
                                />
                                <InputComponent
                                    id={`${id}_swift`}
                                    label="Swift"
                                    register={register}
                                    value={(paymentData as BankTransfer).swift}
                                    onChange={(value) => handleUpdatePaymentMethod(value, 'swift')}
                                    isInvalid={errors[`${id}_swift`] ?? null}
                                    minLength={8}
                                    maxLength={11}
                                    isRequired
                                    flex={1}
                                />
                            </Stack>
                        </>
                    );
                case PaymentTypesTag.TRAVEL_FUND:
                    return (
                        <UploadComponent
                            control={control}
                            id={`${id}_travelAccount`}
                            title="Conto viaggi"
                            subtitle="Carica documento di setup"
                            uploadedFile={(paymentData as TravelAccount).attachments}
                            updateFile={handleUpdateTravelAccountFile}
                            deleteFile={handleDeleteTravelAccountFile}
                            error={errors[`${id}_travelAccount`] ?? null}
                            isInvalid={errors[`${id}_travelAccount`] ? true : false}
                            isRequired
                            maxSize={1048576}
                            sizeString="1 MB"
                        />
                    );
                default:
                    return null;
            }
        }
    }, [
        control,
        Object.keys(errors),
        handleUpdatePaymentMethod,
        id,
        paymentMethod.paymentData,
        paymentMethod.paymentMethodId,
        register,
        state.paymentType,
    ]);

    return (
        <>
            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                <RadioComponent
                    id={`${id}_paymentMethods`}
                    register={register}
                    name={`${labelRadio}${isRequired ? '*' : ''}`}
                    items={paymentMethodsOptions}
                    selected={paymentMethod.paymentMethodId}
                    onChange={handlePaymentMethodId}
                    direction="column"
                    isInvalid={errors[`${id}_paymentMethods`] ?? null}
                    isRequired
                />
            </Stack>
            {paymentMethodForm}
        </>
    );
};

export default PaymentMethodForm;
